import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

export function AssessSjt({ token, info, onDone }) {
  const { data } = useQuery({ queryKey: ["assess-items", token], queryFn: () => get(`/public/assess/${token}/items`), staleTime: 0 });
  const [answers, setAnswers] = useState(null);
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const shown = useRef(Date.now());
  useEffect(() => { if (data && !answers) { setAnswers(data.sjt_answers); const f = data.sjt.findIndex((s) => !data.sjt_answers[s.id]); setI(f === -1 ? data.sjt.length - 1 : f); } }, [data, answers]);
  useEffect(() => { shown.current = Date.now(); window.scrollTo({ top: 0 }); }, [i]);
  if (!data || !answers) return <Skel className="h-80" />;
  const s = data.sjt[i];
  const last = i === data.sjt.length - 1;

  const next = async () => {
    if (!answers[s.id]) { toast.error("Please choose one option."); return; }
    setBusy(true);
    try {
      const ms = Date.now() - shown.current;
      await api.post(`/public/assess/${token}/sjt`, { responses: { [s.id]: { option_id: answers[s.id], ms } }, elapsed_ms: ms });
      if (last && info?.extra_count > (info?.extra_pre || 0)) onDone("extra"); else if (last) { await api.post(`/public/assess/${token}/submit`); onDone("done"); } else setI(i + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="assess-sjt">
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-ink-muted"><span>Scenario {i + 1} of {data.sjt.length}</span><span>Almost there</span></div>
        <Progress value={(100 * (i + 1)) / data.sjt.length} className="h-2 bg-brand-tint" />
      </div>
      <article className="panel space-y-4 p-5 sm:p-6" data-testid={`sjt-${s.id}`}>
        <p className="text-xs uppercase tracking-wide text-ink-muted">Workplace scenario</p>
        <p className="text-base leading-relaxed text-ink">{s.scenario}</p>
        <div className="space-y-2">
          {s.options.map((o) => (
            <button key={o.id} type="button" onClick={() => setAnswers({ ...answers, [s.id]: o.id })} data-testid={`sjt-option-${o.id}`}
              className={cn("w-full rounded-[6px] border p-4 text-left text-sm leading-relaxed transition-colors duration-150",
                answers[s.id] === o.id ? "border-brand bg-brand-tint text-ink" : "border-line bg-white text-ink-secondary hover:border-brand/40")}>
              {o.text}
            </button>
          ))}
        </div>
      </article>
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" disabled={i === 0 || busy} onClick={() => setI(i - 1)} data-testid="sjt-prev-btn">Back</Button>
        <Button className="h-12 flex-1 text-base sm:flex-none sm:px-10" disabled={busy} onClick={next} data-testid="sjt-next-btn">
          {busy && <Loader2 size={16} className="animate-spin" />}{last ? "Submit my answers" : "Next scenario"}
        </Button>
      </div>
    </div>
  );
}
